// src/pages/Admin/DossierInscription/dossier.helpers.js

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

export const CHUNK = 60;

export const BOOLEAN_FILTERS = [
  { value: "", label: "Tous" },
  { value: "true", label: "Validés" },
  { value: "false", label: "Non validés" },
];

export function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.dossiers)) return data.dossiers;
  }

  return [];
}

export function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getRoleId(user) {
  return Number(user?.role_id || user?.roleId || user?.role?.id || 0);
}

export function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function neighborsFromSeason(season) {
  const match = /^(\d{4})\s*-\s*(\d{4})$/.exec(String(season || ""));

  if (!match) return [season].filter(Boolean);

  const y1 = Number(match[1]);
  const y2 = Number(match[2]);

  return [`${y1 - 1}-${y2 - 1}`, `${y1}-${y2}`, `${y1 + 1}-${y2 + 1}`];
}

export function formatDateFr(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export function getDossierId(dossier) {
  return dossier?.id ?? dossier?.dossier_id ?? dossier?.DossierInscription?.id;
}

export function getAdherentId(dossier) {
  return dossier?.inscription_adherent_id || dossier?.Adherent?.id;
}

export function getAdherentName(dossier) {
  const adherent = dossier?.Adherent || {};
  const nom = String(adherent?.nom || "")
    .trim()
    .toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
}

export function getGroupsText(dossier) {
  const groups = Array.isArray(dossier?.Adherent?.Groups)
    ? dossier.Adherent.Groups
    : [];

  return groups
    .map((group) => group?.nom)
    .filter(Boolean)
    .join(", ");
}

export function toAbsoluteUrl(url = "") {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    return `${API_BASE}${value}`;
  }

  if (value.startsWith("/")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

export function isDriveUrl(url = "") {
  return /^https?:\/\/(drive\.google\.com|docs\.google\.com)\//i.test(
    String(url || ""),
  );
}

export function isServerFile(url = "") {
  const value = String(url || "");

  return (
    value.startsWith("/uploads/") || value.startsWith(`${API_BASE}/uploads/`)
  );
}

export function getBoolDisplay(value) {
  return value
    ? {
        label: "Oui",
        className:
          "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
      }
    : {
        label: "Non",
        className:
          "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
      };
}

export function getDossierCompletion(dossier) {
  const signedForm =
    dossier?.formulaire_licence_signe || dossier?.formulaire_licence_signee;

  const checks = [
    Boolean(dossier?.validate_cm),
    Boolean(signedForm),
    Boolean(dossier?.validate_reglement),
  ];

  const done = checks.filter(Boolean).length;

  return {
    done,
    total: checks.length,
    percent: Math.round((done / checks.length) * 100),
    complete: done === checks.length,
  };
}

export async function runWithConcurrency(items, worker, limit = 6) {
  const results = new Array(items.length);
  let index = 0;

  const runners = new Array(Math.min(limit, items.length))
    .fill(0)
    .map(async () => {
      while (index < items.length) {
        const currentIndex = index;
        index += 1;
        results[currentIndex] = await worker(items[currentIndex], currentIndex);
      }
    });

  await Promise.all(runners);

  return results;
}

/**
 * Retourne les éléments encore manquants dans le dossier d'inscription.
 *
 * On distingue :
 * - document absent ;
 * - document présent mais non encore validé ;
 * - règlement non validé.
 */
export function getMissingDossierItems(dossier) {
  const missing = [];

  /* ------------------------------------------------------------------------ */
  /* Certificat médical                                                       */
  /* ------------------------------------------------------------------------ */

  if (!dossier?.cm_url) {
    missing.push({
      key: "cm",
      label: "Certificat médical / attestation médicale",
      type: "missing",
    });
  } else if (!dossier?.validate_cm) {
    missing.push({
      key: "cm_validation",
      label: "Certificat médical en attente de validation",
      type: "pending",
    });
  }

  /* ------------------------------------------------------------------------ */
  /* Formulaire de licence signé                                              */
  /* ------------------------------------------------------------------------ */

  const signedForm =
    dossier?.formulaire_licence_signe || dossier?.formulaire_licence_signee;

  if (!signedForm) {
    missing.push({
      key: "signed_form",
      label: "Formulaire de licence signé",
      type: "missing",
    });
  }

  /* ------------------------------------------------------------------------ */
  /* Règlement                                                                */
  /* ------------------------------------------------------------------------ */

  if (!dossier?.validate_reglement) {
    missing.push({
      key: "reglement",
      label: "Règlement de l'inscription",
      type: "missing",
    });
  }

  return missing;
}
